import AbstractRange from "@/core/types/AbstractRange";
import type { RangeToStringOptions } from "@/core/types/AbstractRange";
import DateSimple    from "@/core/types/DateSimple";

/**
 * Диапазон дат (без времени)
 */
class DateRange extends AbstractRange<DateSimple> {
  constructor(start: DateSimple | Date | string | null = null, end: DateSimple | Date | string | null = null) {
    // Конвертируем в DateSimple если нужно
    const convertedStart = start ? (start instanceof DateSimple ? start : new DateSimple(start)) : null;
    const convertedEnd = end ? (end instanceof DateSimple ? end : new DateSimple(end)) : null;

    super(convertedStart, convertedEnd);
  }

  static fromServer(data) {
      const start = data.start ? DateSimple.fromServer(data.start) : null;
      const end = data.end ? DateSimple.fromServer(data.end) : null;

      return new DateRange(start, end);
  }

  static isDateRange(value:any): value is DateRange {
    return value instanceof DateRange;
  }

  /**
   * Создание DateRange из объекта
   * Переопределяет метод родителя для корректной работы с датами
   */
  static fromObject(obj: { start?: DateSimple | Date | string | null; end?: DateSimple | Date | string | null } | null): DateRange | null {
    if (!obj) {
      throw new Error('DateRange.fromObject: объект не может быть null или undefined');
    }
    return new DateRange(obj.start || null, obj.end || null);
  }


  /**
   * Сеттер для start с автоматической конвертацией в DateSimple
   * Это нужно для наших стареньких компонентов типа VDAteRAnge и тд. нужно чтобы
   * был метод форматирования у даты
   */
  override set start(value: DateSimple | Date | string | null) {
    if (value === null) {
      super.start = null;
    } else if (value instanceof DateSimple) {
      super.start = value;
    } else {
      super.start = new DateSimple(value);
    }
  }

  /**
   * Геттер для start
   */
  override get start(): DateSimple | null {
    return super.start;
  }

  /**
   * Сеттер для end с автоматической конвертацией в DateSimple
   * Это нужно для наших стареньких компонентов типа VDAteRAnge и тд. нужно чтобы
   * был метод форматирования у даты
   */
  override set end(value: DateSimple | Date | string | null) {
    if (value === null) {
      super.end = null;
    } else if (value instanceof DateSimple) {
      super.end = value;
    } else {
      super.end = new DateSimple(value);
    }
  }

  /**
   * Геттер для end
   */
  override get end(): DateSimple | null {
    return super.end;
  }


  /**
   * Создаёт копию диапазона.
   *
   * Границы клонируются: мутация дат копии не затрагивает оригинал.
   * Полуоткрытые и пустые диапазоны копируются как есть.
   *
   * Прямое `new DateRange(range.start, range.end)` копией не является —
   * конструктор пропускает готовые DateSimple без преобразования, и обе
   * ссылки останутся общими.
   *
   * @example
   * const copy = range.clone();
   * copy.start.setDate(1); // оригинал не изменится
   */
  clone(): DateRange {
    let start: DateSimple | null;
    let end:   DateSimple | null;

    if (this._start === null || this._start === undefined) {
      start = null;
    } else {
      start = this._start.clone();
    }

    if (this._end === null || this._end === undefined) {
      end = null;
    } else {
      end = this._end.clone();
    }

    return new DateRange(start, end);
  }

  /**
   * Возвращает количество дней в диапазоне
   */
  getDaysCount(): number {
    if (this.isEmpty()) {
      return 0;
    }
    return this._end!.diffInDays(this._start!) + 1;
  }

  /**
   * Возвращает новый DateRange со сдвигом обоих концов на указанное число дней.
   * Исходный диапазон не мутируется.
   *
   * Если у диапазона нет start или end — кидает ошибку: сдвиг определён
   * только для полностью заданного диапазона.
   *
   * @param days — количество дней для сдвига. Может быть отрицательным.
   *
   * @example
   * // Расчёт периода приёмки на основе периода отгрузки и дней доставки
   * const shipmentPeriod = new DateRange('2026-01-10', '2026-01-15');
   * const receiptPeriod  = shipmentPeriod.addDays(3);
   * // → DateRange('2026-01-13', '2026-01-18')
   *
   * @example
   * // Сдвиг назад
   * const previousWeek = currentWeek.addDays(-7);
   */
  addDays(days: number): DateRange {
    if (this._start === null || this._end === null) {
      throw new Error('DateRange.addDays: диапазон должен иметь обе границы (start и end)');
    }

    return new DateRange(this._start.addDays(days), this._end.addDays(days));
  }

  /**
   * Строковое представление диапазона дат: "DD.MM - DD.MM.yyyy".
   * Год слева опускается, если совпадает с правым. Полуоткрытые диапазоны
   * форматируются через ту же сборку частей: недостающая граница просто
   * не добавляет свой текст (дата/prefix/middle/suffix для неё пропускаются).
   *
   * Параметры передаются единым объектом — для совместимости сигнатуры с базой.
   * Три позиции собираются как "{prefix} {start}{ middle }{end} {suffix}".
   *
   * @param options.prefix слово перед началом (например "с"). По умолчанию пусто.
   * @param options.middle разделитель/слово между датами. По умолчанию "-".
   * @param options.suffix слово после конца (например "включительно"). По умолчанию пусто.
   *
   * Примеры:
   *   range.toString()                                          → "10.01 - 15.01.2026"
   *   range.toString({ middle: '—' })                           → "10.01 — 15.01.2026"
   *   range.toString({ prefix: 'с', middle: 'по' })             → "с 10.01 по 15.01.2026"
   *   range.toString({ prefix: 'с', middle: 'по', suffix: 'включительно' })
   *                                                             → "с 10.01 по 15.01.2026 включительно"
   */
  override toString(options: RangeToStringOptions = {}): string {
    //Слава сказал пока захардкодить по умолчанию prefix 'с' и middle 'по'
    const prefix = options.prefix ?? 'с';
    const middle = options.middle ?? 'по';
    const suffix = options.suffix ?? '';

    const format = 'DD.MM.yyyy';

    if (!this._start && !this._end) { return ''; }

    const isSameYear = this._start && this._end
      && this._start.getFullYear() === this._end.getFullYear();
    const leftFormat = isSameYear ? 'DD|MM' : format;

    const left = this._start ? this._start.format(leftFormat) : '';
    const right = this._end ? this._end.format(format) : '';

    return this._joinParts(left, right, prefix, middle, suffix);
  }

  /**
   * @deprecated используйте toString()
   */
  getStringRepresentation(): string {
    return this.toString();
  }
}

export default DateRange;
